import React, { useState, useRef, useEffect } from 'react';
import ChevronDownIcon from '@/assets/Chevrondown.svg';
import './MultiSelectDrodpdown.css';

const MultiSelectDrodpdown = ({
  items = [],
  options = [],
  values = [],
  value = [],
  onChange,
  placeholder = "Select options",
  className = "",
  buttonClassName = "",
  disabled = false,
  labelKey = "label",
  valueKey = "value",
  showSelectAll = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const selectAllRef = useRef(null);

  // Support both items and options props
  const dataItems = items.length > 0 ? items : options;
  const selectedValues = values.length > 0 ? values : value;
  const safeSelectedValues = Array.isArray(selectedValues) ? selectedValues : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if clicking outside
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };

    if (isOpen) {
      // Use mousedown to fire before FilterPanel's handler
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Map items to standard format
  const mappedOptions = dataItems.map(item => ({
    label: String(item[labelKey] || item.label || ''),
    value: String(item[valueKey] || item.value || item.id || '')
  }));

  const filteredOptions = mappedOptions.filter(option =>
    option.label && typeof option.label === 'string' && option.label.toLowerCase().includes(search.toLowerCase())
  );

  const allFilteredValues = filteredOptions.map(opt => opt.value);
  const selectedFilteredValues = safeSelectedValues.filter(val => 
    allFilteredValues.includes(String(val))
  );
  
  const isAllSelected = filteredOptions.length > 0 && selectedFilteredValues.length === allFilteredValues.length;
  const isSomeSelected = selectedFilteredValues.length > 0 && selectedFilteredValues.length < allFilteredValues.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSomeSelected;
    }
  }, [isSomeSelected]);

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      const newValue = safeSelectedValues.filter(val => !allFilteredValues.includes(String(val)));
      onChange([...new Set(newValue)]);
      return;
    }

    const originalFilteredValues = dataItems
      .filter(item => {
        const mVal = String(item[valueKey] || item.value || item.id || '');
        return allFilteredValues.includes(mVal);
      })
      .map(item => item[valueKey] || item.value || item.id || '');

    const otherSelected = safeSelectedValues.filter(val => !allFilteredValues.includes(String(val)));
    onChange([...new Set([...otherSelected, ...originalFilteredValues])]);
  };

  const selectedLabels = Array.isArray(selectedValues)
    ? selectedValues
        .map(val => {
          const hit = mappedOptions.find(opt => String(opt.value) === String(val));
          return hit?.label || '';
        })
        .filter(Boolean)
    : [];

  // Truncate display: show first 2 labels + "+N more"
  const getDisplayText = () => {
    if (selectedLabels.length === 0) return null;
    if (selectedLabels.length <= 2) return selectedLabels.join(', ');
    return `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2}`;
  };

  const handleToggle = (optionValue) => {
    // Ensure type consistency for comparison
    const normalizedSelected = selectedValues.map(v => String(v));
    const normalizedOption = String(optionValue);
    
    const newValue = normalizedSelected.includes(normalizedOption)
      ? selectedValues.filter(v => String(v) !== normalizedOption)
      : [...selectedValues, optionValue];
    
    // Ensure no duplicates
    const uniqueValues = [...new Set(newValue)];
    
    onChange(uniqueValues);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleDropdownToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  return (
    <div className={`dropdown ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`dropdown__trigger ${isOpen ? 'dropdown__trigger--open' : ''} ${selectedValues.length > 0 ? 'has-value' : ''} ${buttonClassName}`}
        onClick={handleDropdownToggle}
        disabled={disabled}
      >
        <span className="dropdown__trigger-text">
          {selectedValues.length === 0 ? (
            <span className="dropdown__placeholder">{placeholder}</span>
          ) : (
            <span className="dropdown__selected-text" title={selectedLabels.join(', ')}>
              {selectedLabels.length === 1 
                ? (selectedLabels[0].length > 35 ? `${selectedLabels[0].substring(0, 32)}...` : selectedLabels[0])
                : selectedLabels.length > 1 
                  ? `${selectedLabels[0].length > 20 ? `${selectedLabels[0].substring(0, 17)}...` : selectedLabels[0]} +${selectedLabels.length - 1} more`
                  : `${selectedValues.length} selected`
              }
            </span>
          )}
        </span>
        <img 
          src={ChevronDownIcon}
          alt="chevron"
          className={`dropdown__arrow ${isOpen ? "rotated" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="dropdown__menu">
          <div className="dropdown__search">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="dropdown__search-input"
            />
          </div>

          {showSelectAll && filteredOptions.length > 1 && (
            <div className="dropdown__select-all" style={{ borderBottom: '1px solid var(--color-border-light)' }}>
              <label className="dropdown__option dropdown__option--multi" style={{ margin: 0, borderRadius: 0, padding: '8px 12px' }}>
                <input
                  type="checkbox"
                  ref={selectAllRef}
                  checked={isAllSelected}
                  onChange={handleSelectAllToggle}
                />
                <span style={{ fontWeight: '600' }}>
                  {isAllSelected ? 'Deselect All' : 'Select All'}
                </span>
              </label>
            </div>
          )}
          
          <div className="dropdown__options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <label key={option.value} className="dropdown__option dropdown__option--multi" title={option.label}>
                  <input
                    type="checkbox"
                    checked={selectedValues.map(v => String(v)).includes(String(option.value))}
                    onChange={() => handleToggle(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))
            ) : (
              <div className="dropdown__no-options">No options available</div>
            )}
          </div>

          {selectedValues.length > 0 && (
            <div className="dropdown__actions">
              <button type="button" className="dropdown__clear-btn" onClick={handleClearAll}>
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDrodpdown;
